import moment from 'moment';

import { DateFormat, toJsDate } from '../../../../utils/date';
import { isFutureStartDate, toCalendarDay } from '../../../utils/userContract';
import {
  AttentionTag,
  DarkTag,
  InfoTag,
  OkTag,
} from '../../../../components/Tags';

import texts from './texts';

const CATEGORY_CONFIG = {
  ONE_ON_ONE_CORE: { label: texts.categories.ONE_ON_ONE_CORE, Tag: DarkTag },
  PROMO: { label: texts.categories.PROMO, Tag: InfoTag },
  ENTRY: { label: texts.categories.ENTRY, Tag: OkTag },
  LOW_TICKET: { label: texts.categories.LOW_TICKET, Tag: AttentionTag },
};

/**
 * What kind of offering sits in an Inside Sales category.
 */
const OfferingType = {
  PACKAGE: 'PACKAGE',
  CHALLENGE: 'CHALLENGE',
};

/**
 * The only category a challenge may be assigned to. Every other category takes a package.
 */
const CHALLENGE_CATEGORY = 'LOW_TICKET';

/**
 * How a challenge's enrollment window reads at a glance.
 */
const AcceptUntilState = {
  OPEN: 'OPEN',
  CLOSING_SOON: 'CLOSING_SOON',
  CLOSED: 'CLOSED',
};

// A challenge inside this many days of its cut-off is flagged, so a rep does not open a
// conversation about an offer that is about to stop being sellable.
const CLOSING_SOON_DAYS = 3;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * A challenge's enrollment deadline as the offering screens show it: 'Nov 9, 2026'.
 *
 * @param {object|Date|string} [acceptUntilValue] The stored accept-until value.
 * @returns {string} The formatted date, or '' when the challenge has no deadline.
 */
const formatAcceptUntil = (acceptUntilValue) => {
  const acceptUntil = toJsDate(acceptUntilValue);

  return acceptUntil ? moment(acceptUntil).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_UNPADDED_FORMAT) : '';
};

/**
 * Where a challenge stands against its start date, as the offering screens show it.
 *
 * The start date is a bare calendar day ('2026-11-09'), so it is read as the day the coach picked
 * rather than as an instant: it neither slips a day in timezones behind UTC nor flips to ongoing the
 * evening before. A challenge that has begun reads as ongoing rather than by a date that is behind us.
 *
 * @param {string|object} [startDateValue] The stored challenge start day.
 * @returns {{isOngoing: boolean, date: string}|null} The start, or null when the challenge has none.
 *   `date` is formatted like the accept-until date: 'Nov 9, 2026'.
 */
const getChallengeStart = (startDateValue) => {
  const startDay = toCalendarDay(startDateValue);
  const start = startDay && moment(startDay, DateFormat.DEFAULT_DATE_FORMAT, true);
  if (!start?.isValid()) {
    return null;
  }

  return {
    isOngoing: !isFutureStartDate(startDay),
    date: start.format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_UNPADDED_FORMAT),
  };
};

/**
 * Classify a challenge's enrollment window so the offering can be shaded to match.
 *
 * An offering with no cut-off never closes, so it reads as open.
 *
 * @param {object|Date|string} [acceptUntilValue] The stored accept-until value.
 * @param {Date} [now] The moment to compare against.
 * @returns {string} One of AcceptUntilState.
 */
const getAcceptUntilState = (acceptUntilValue, now = new Date()) => {
  const acceptUntil = toJsDate(acceptUntilValue);
  if (!acceptUntil) {
    return AcceptUntilState.OPEN;
  }
  if (acceptUntil.getTime() < now.getTime()) {
    return AcceptUntilState.CLOSED;
  }
  const daysLeft = (acceptUntil.getTime() - now.getTime()) / MS_PER_DAY;
  return daysLeft <= CLOSING_SOON_DAYS ? AcceptUntilState.CLOSING_SOON : AcceptUntilState.OPEN;
};

/**
 * Whether a challenge contract can still be offered to a lead.
 *
 * A challenge without an accept-until date has no cut-off and stays offerable; once the date has
 * passed the challenge drops out of the picker and out of the lead modal, so no rep can sell a
 * round that has already closed.
 *
 * @param {object} contract The challenge's BASE contract.
 * @param {Date} [now] The moment to compare against.
 * @returns {boolean} True while the challenge is still accepting enrollments.
 */
const isChallengeStillAccepting = (contract, now = new Date()) => (
  getAcceptUntilState(contract?.acceptUntil, now) !== AcceptUntilState.CLOSED
);

export {
  AcceptUntilState,
  CATEGORY_CONFIG,
  CHALLENGE_CATEGORY,
  OfferingType,
  formatAcceptUntil,
  getAcceptUntilState,
  getChallengeStart,
  isChallengeStillAccepting,
};
